import { format } from "date-fns/format";
import { isSameDay } from "date-fns/isSameDay";
import { nb } from "date-fns/locale/nb";
import { HStack, Text } from "shared/components/Spor";
import { useTheme } from "styled-components";

/* 
  This component is used to display a time interval in the format "HH:mm - HH:mm (dd.MM.yy)".
  
  But "HH:mm (dd.MM.yy)- HH:mm (dd.MM.yy)" if different dates.

  Use this whenever you display a time interval that could be across dates.
*/

type TimeIntervalProps = {
  startTime: Date | null;
  endTime: Date | null;
  disabled?: boolean;
};

export const TimeInterval = ({
  startTime,
  endTime,
  disabled,
}: TimeIntervalProps) => {
  if (!startTime || !endTime) {
    return null;
  }
  const theme = useTheme();
  const mainTextColor = disabled
    ? theme.colorTextDisabled
    : theme.colorTextMain;
  const secondaryTextColor = disabled
    ? theme.colorTextDisabled
    : theme.colorTextTertiary;

  const isDifferentDate = !isSameDay(startTime, endTime);

  return (
    <HStack gap={0} flexWrap="wrap" alignItems="baseline">
      <Text color={mainTextColor} variant="xs">
        {format(startTime, "HH:mm", { locale: nb })}
      </Text>

      {isDifferentDate ? (
        <Text color={secondaryTextColor} pl={1} variant="xs">
          ({format(startTime, "dd.MM.yy", { locale: nb })})
        </Text>
      ) : null}

      <Text color={mainTextColor} variant="xs">
        –
      </Text>

      <Text color={mainTextColor} pr={1} variant="xs">
        {format(endTime, "HH:mm", { locale: nb })}
      </Text>

      <Text color={secondaryTextColor} variant="xs">
        ({format(endTime, "dd.MM.yy", { locale: nb })})
      </Text>
    </HStack>
  );
};
